import { createColumnHelper } from '@tanstack/react-table';
import React, { ChangeEvent, useRef } from 'react';
import Dropzone from 'react-dropzone';
import styled from 'styled-components';

import { FeatureFlag } from '@ndvr/auth-api';
import { FileInfo } from '@ndvr/customer-api';

import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { colors } from 'styles/theme/themeCore';
import { useHouseholdRouteType, useRequiredParams } from 'utils';
import { useFeatureFlag } from 'views/auth/useFeatureFlag';
import {
    useDeleteHouseholdFile,
    useDownloadHouseholdFileUrl,
    useHouseholdClientFilesList,
    useHouseholdFilesList,
    useUploadHouseholdFile,
} from 'views/household/FilesTab/box-queries';
import { CondensedTable, DateTimeDisplay, HorizontalLayout, PageSpinner, Text, Toast, VerticalLayout } from 'widgets';

import { OnboardingFilesSection } from './OnboardingFilesSection';

const columnHelper = createColumnHelper<FileInfo>();

export const FilesTab = () => {
    const fileStorageEnabled = useFeatureFlag(FeatureFlag.GLOBAL_ACCOUNT_FILE_STORAGE);
    const onboardingEnabled = useFeatureFlag(FeatureFlag.ONBOARDING);
    const { isHouseholdRoute } = useHouseholdRouteType();
    const { globalAccountGUID } = useRequiredParams(['globalAccountGUID']);

    return (
        <div className="flex flex-col gap-6">
            {isHouseholdRoute && onboardingEnabled && <OnboardingFilesSection householdGUID={globalAccountGUID} />}
            {fileStorageEnabled && <HouseholdFilesSection />}
        </div>
    );
};

const HouseholdFilesSection = () => {
    const fileRef = useRef<HTMLInputElement | null>(null);
    const { globalAccountGUID } = useRequiredParams(['globalAccountGUID']);
    const deleteHouseholdFileMutation = useDeleteHouseholdFile(globalAccountGUID);
    const { data: files, isPending } = useHouseholdFilesList(globalAccountGUID);
    const { data: clientFiles, isPending: isClientFilesPending } = useHouseholdClientFilesList(globalAccountGUID);
    const { mutate: uploadHouseholdFileMutation, isPending: isUploading } = useUploadHouseholdFile(globalAccountGUID);
    const { mutate: downloadFileUrl } = useDownloadHouseholdFileUrl(globalAccountGUID);

    const columns = [
        columnHelper.accessor('name', {
            id: 'name',
            header: 'Name',
            cell: ({ getValue, row }) => (
                <Text
                    onClick={() => {
                        downloadFileUrl(row.original.fileId, {
                            onSuccess: (response) => {
                                Object.assign(globalThis.document.createElement('a'), {
                                    href: response,
                                    download: getValue(),
                                }).click();
                            },
                        });
                    }}
                >
                    {getValue()}
                </Text>
            ),
            sortingFn: 'alphanumeric',
        }),
        columnHelper.accessor('size', {
            id: 'size',
            header: 'Size (Bytes)',
            sortingFn: 'alphanumeric',
        }),
        columnHelper.accessor('createdAt', {
            id: 'createdAt',
            header: 'Created At',
            cell: ({ getValue }) => <DateTimeDisplay dateString={getValue()} />,
            sortingFn: 'alphanumeric',
        }),
        columnHelper.accessor('name', {
            id: 'delete',
            header: '',
            cell: ({ row }) => {
                const isDeleting =
                    deleteHouseholdFileMutation.isPending &&
                    deleteHouseholdFileMutation.variables === row.original.fileId;

                return (
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={() => deleteHouseholdFileMutation.mutate(row.original.fileId)}
                        disabled={deleteHouseholdFileMutation.isPending}
                        title="Delete"
                    >
                        {isDeleting ? 'Deleting...' : 'Delete'}
                    </Button>
                );
            },
            enableSorting: false,
            size: 150,
        }),
    ];

    const handleUploadClicked = (e: ChangeEvent<HTMLInputElement>) => {
        if (!e.target.files || e.target.files.length === 0) {
            return;
        }
        uploadHouseholdFileMutation(e.target.files[0]);
        e.target.value = '';
    };

    const handleUploadDnd = (fileArray: File[]) => {
        if (fileArray.length !== 1) {
            Toast.info('Only single file upload is supported!');
            return;
        }
        uploadHouseholdFileMutation(fileArray[0]);
    };

    if (isPending || isClientFilesPending) {
        return <PageSpinner />;
    }

    return (
        <>
            <Dropzone onDrop={handleUploadDnd} noClick maxFiles={1}>
                {({ getRootProps, getInputProps, isDragAccept, isDragReject }) => (
                    <DropArea {...getRootProps()} $isDragAccept={isDragAccept} $isDragReject={isDragReject}>
                        <VerticalLayout $gap={24}>
                            <HorizontalLayout $apart>
                                <h2 className="text-cocoa-1000 text-xl">Files Shared with the Client</h2>
                                <Button onClick={() => fileRef.current?.click()} disabled={isUploading}>
                                    {isUploading ? 'Uploading...' : 'Upload File'}
                                </Button>
                                <input
                                    type="file"
                                    ref={fileRef}
                                    onChange={handleUploadClicked}
                                    style={{ display: 'none' }}
                                />
                            </HorizontalLayout>
                            <Card>
                                <CondensedTable data={files || []} columns={columns} />
                            </Card>
                        </VerticalLayout>
                        <input {...getInputProps()} />
                    </DropArea>
                )}
            </Dropzone>
            <Spacer />
            <Wrapper>
                <VerticalLayout $gap={24}>
                    <h2 className="text-cocoa-1000 text-xl">Client Files</h2>
                    <Card>
                        <CondensedTable data={clientFiles || []} columns={columns} />
                    </Card>
                </VerticalLayout>
            </Wrapper>
        </>
    );
};

type DropAreaProps = { $isDragAccept: boolean; $isDragReject: boolean };

const getColor = (props: DropAreaProps) => {
    if (props.$isDragAccept) {
        return colors.green300;
    }
    if (props.$isDragReject) {
        return colors.red300;
    }
    return colors.cocoa100;
};

const DropArea = styled.div<DropAreaProps>`
    align-items: center;
    padding: 20px;
    border-width: 2px;
    border-radius: 2px;
    border-color: ${(props) => getColor(props)};
    border-style: dashed;
    background-color: ${colors.cocoa0};
    outline: none;
    transition: border 0.24s ease-in-out;
`;

const Spacer = styled.div`
    height: 1.5rem;
`;

const Wrapper = styled.div`
    padding: 20px;
`;
